/**
 * What QuickBooks is told about a job, and when.
 *
 * The books open at the moment a job is **sold**, not when an estimate is sent.
 * Pressing Sends is that moment: `send_job()` stamps `jobs.sends_at`, the
 * approved checklist appears, and the job is handed to production. An estimate
 * that has merely been presented is a quote — half of them never close, and
 * writing one into the accounts would book revenue and a receivable for work
 * nobody has agreed to. See docs/adr/0038.
 *
 * This module is the mapping and nothing else: no network, no tokens, no
 * Supabase. It turns rows this application already holds into the shapes the
 * QuickBooks Online API accepts, and it is the only place money crosses that
 * boundary. The Edge Function transmits what these functions produce and never
 * computes an amount of its own — a second implementation of the ladder, in
 * Deno, out of reach of `packages/core/test/ladder.test.ts`, is exactly the
 * drift CLAUDE.md rule 1 exists to prevent.
 *
 * Money arrives here as integer cents (rule 3) and leaves as the decimal
 * QuickBooks insists on, in one function, once.
 */

import { type Cents, addCents, toDecimal } from "./money.js";

/**
 * The contract amount, as QuickBooks wants it: a decimal with at most two
 * places.
 *
 * `toDecimal` divides integer cents by 100, so 1_089_81 becomes 1089.81
 * exactly. Anything that reaches the API as a float built by multiplying
 * dollars is a cent adrift sooner or later, and a penny of drift on an invoice
 * is a reconciliation somebody has to do by hand.
 */
export function qboAmount(value: Cents): number {
  return toDecimal(value);
}

/** One signature, and every option it bought. */
export interface SignedContract {
  /**
   * The acceptance row's id. What the sum is taken over: one signature is one
   * amount however many options it named, and the same option signed twice is
   * still one debt.
   */
  id: string;
  /** `proposal_acceptances.total`, frozen at signature and never edited. */
  total: Cents;
  /** What it bought: `estimate_id`, then `extra_estimate_ids` (ADR 0120). */
  estimateIds: readonly string[];
  /** `accepted_at`. The newest signature on an option is the one that counts. */
  acceptedAt: string;
}

/** One sold option, and what its own ladder says it is worth. */
export interface SoldOption {
  /** `estimates.id`. */
  estimateId: string;
  /** `computeLadder().grandTotal` for this option alone. */
  ladderTotal: Cents;
  /** Signed changes on this option; only those after its governing proposal add to it. */
  signedChangeOrders?: readonly { amount: Cents; signedAt: string }[];
}

/**
 * What the customer is invoiced for: every option they are buying, added up.
 *
 * Each option is worth one of two things, in order of authority:
 *
 * 1. **What they signed for it.** `proposal_acceptances.total` is frozen at
 *    signature and can never be edited (ADR 0032). If a customer put their
 *    name to $18,400, that is the invoice, whatever the estimate says today —
 *    the job carries on being edited after signing, deliberately, and those
 *    edits are not a price change the customer agreed to.
 * 2. **The ladder**, for an option sold on paper or agreed by hand.
 *    `computeLadder().grandTotal`, because a multi-option job sells two
 *    estimates at once and they add (ADR 0030, `jobs.multi_option`).
 *
 * ## Why this is a sum and not a choice
 *
 * It used to be a choice: one signed total, or the ladder, never both. That
 * read one signature per job, and a job can have several. Katie Harding-Smith
 * on job 1015 signed the deck at $27,911.78 on 2026-09-04 and the siding at
 * $4,000 on 2026-09-09, on two documents, because the Present screen could
 * only take one option per signature at the time. The latest signature won, so
 * her Balance card read a $4,000 contract, called a job she owed $17,355.89 on
 * "Paid up", and would have billed QuickBooks for the siding alone. Two more
 * options on the same job had been agreed without a signature at all, and the
 * old rule could not see those either: a signed total suppressed the ladder
 * outright, so a part-signed job lost whatever was not on the last document.
 *
 * ## One signature per option, the newest
 *
 * Signatures are NOT simply added: the same option gets signed again whenever a
 * document is re-sent, and job 1004's single $16,091.72 estimate carries two
 * acceptances for exactly that reason. So each option takes the newest
 * signature naming it, and the sum is over the distinct signatures that
 * survive — never over the rows.
 *
 * An option nobody signed contributes its ladder. An option covered by a
 * signature contributes nothing of its own; its signature carries it, along
 * with anything else that signature bought.
 *
 * With no options at all this falls back to every signature, so a job whose
 * estimates have been deactivated still reports what was signed rather than
 * dropping to zero.
 */
export function invoiceAmount(input: {
  /** Every e-signature on the job. Empty for a job sold on paper. */
  signed?: readonly SignedContract[] | null | undefined;
  /** Every option being sold — the active estimates — and its ladder. */
  options?: readonly SoldOption[] | null | undefined;
}): Cents {
  const options = input.options ?? [];
  // Newest first, so the first signature to claim an option is the one that
  // counts and every older one for it falls away.
  //
  // A signature worth nothing claims nothing: it is a row with no money on it,
  // and letting it cover an option would zero out work the ladder can price.
  // That has been the rule since this function took a single signed total.
  const signed = [...(input.signed ?? [])]
    .filter((contract) => contract.total > 0)
    .sort((a, b) => signedAt(b) - signedAt(a));

  // A signature can buy several options for one frozen total. If a later
  // signature replaces only some of them, neither row says how much of the
  // older total belongs to the options that remain. Adding both full totals
  // would overbill; using today's ladder would invent a price they did not
  // sign. Stop here until a person reconciles the documents.
  const activeIds = options.length
    ? new Set(options.map((option) => option.estimateId))
    : null;
  const seen = new Set<string>();
  for (const contract of signed) {
    const relevant = activeIds
      ? contract.estimateIds.filter((id) => activeIds.has(id))
      : contract.estimateIds;
    const fresh = relevant.filter((id) => !seen.has(id));
    if (
      fresh.length > 0 &&
      (fresh.length !== relevant.length ||
        (activeIds !== null && relevant.length !== contract.estimateIds.length))
    ) {
      throw new Error(
        "Signed documents overlap only in part; their totals need review before billing.",
      );
    }
    for (const id of relevant) seen.add(id);
  }

  const coveredBy = new Map<string, string>();
  for (const contract of signed) {
    for (const estimateId of contract.estimateIds) {
      if (!coveredBy.has(estimateId)) coveredBy.set(estimateId, contract.id);
    }
  }

  const counted = new Set<string>();
  for (const option of options) {
    const contractId = coveredBy.get(option.estimateId);
    if (contractId != null) counted.add(contractId);
  }

  // Nothing active to hang a signature on. Report what was signed rather than
  // nothing at all.
  if (options.length === 0) {
    return addCents(...newestPerOption(signed).map((c) => c.total));
  }

  return addCents(
    ...signed.filter((c) => counted.has(c.id)).map((c) => c.total),
    ...options
      .filter((option) => !coveredBy.has(option.estimateId))
      .map((option) => option.ladderTotal),
    ...options.flatMap((option) => {
      const contract = signed.find(
        (candidate) => candidate.id === coveredBy.get(option.estimateId),
      );
      if (!contract) return [];
      const signedAtContract = signedAt(contract);
      return (option.signedChangeOrders ?? [])
        .filter((change) => Date.parse(change.signedAt) > signedAtContract)
        .map((change) => change.amount);
    }),
  );
}

/**
 * The contract as it stood when the customer signed it.
 *
 * `invoiceAmount` with the changes signed since left out — so it answers "what
 * did they put their name to", where `invoiceAmount` answers "what do they owe
 * altogether". The two are the same figure until somebody signs a change
 * order.
 *
 * It exists for one reader: the default first payment. The deposit is half of
 * what was signed and has to stay half of what was signed, because on a job
 * where nobody typed a figure it is worked out afresh on every read — so
 * without this a change order raises a deposit that was paid weeks ago, and
 * the Final, being the remainder, rises by half the change instead of all of
 * it (ADR 0205).
 *
 * An option with no signature is worth its ladder here as it is there, and
 * that ladder already carries its change orders. There is nothing frozen on
 * such an option to hold a schedule to, and its schedule has always followed
 * the estimate.
 */
export function contractAsSigned(input: {
  signed?: readonly SignedContract[] | null | undefined;
  options?: readonly SoldOption[] | null | undefined;
}): Cents {
  return invoiceAmount({
    signed: input.signed,
    options: (input.options ?? []).map((option) => ({
      ...option,
      signedChangeOrders: [],
    })),
  });
}

/**
 * The signature one estimate was sold on: the newest naming it, of those with
 * money on them. The same signature `invoiceAmount` and `contractAsSigned` let
 * carry it, so whatever reads this agrees with the pink permit form's
 * valuation box (ADR 0289). Null on an estimate nobody has signed.
 */
export function signatureFor(
  estimateId: string,
  signed: readonly SignedContract[] | null | undefined,
): SignedContract | null {
  let newest: SignedContract | null = null;
  for (const contract of signed ?? []) {
    if (contract.total <= 0 || !contract.estimateIds.includes(estimateId))
      continue;
    if (!newest || signedAt(contract) > signedAt(newest)) newest = contract;
  }
  return newest;
}

/** `accepted_at` as a number, and 0 for anything unreadable. */
function signedAt(contract: SignedContract): number {
  const at = Date.parse(contract.acceptedAt ?? "");
  return Number.isFinite(at) ? at : 0;
}

/**
 * The distinct signatures left once every option keeps only its newest.
 *
 * `signed` must already be newest first.
 */
function newestPerOption(signed: readonly SignedContract[]): SignedContract[] {
  const claimed = new Set<string>();
  const kept: SignedContract[] = [];
  for (const contract of signed) {
    const isNewest = contract.estimateIds.some((id) => !claimed.has(id));
    // A signature naming no option at all cannot be superseded by one that
    // does, so it stands on its own.
    if (isNewest || contract.estimateIds.length === 0) kept.push(contract);
    for (const id of contract.estimateIds) claimed.add(id);
  }
  return kept;
}

/** The parts of a contact QuickBooks needs. Anything with these fields will do. */
export interface InvoiceableContact {
  full_name?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  company?: string | null;
  email?: string | null;
  phone?: string | null;
  cell?: string | null;
}

/** The parts of a job QuickBooks needs. */
export interface InvoiceableJob {
  job_number?: number | string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
}

/**
 * QuickBooks' display name for a customer, which is its unique key.
 *
 * Two rules of theirs, both of which bite in this data:
 *
 * - **A colon is not a character, it is a separator.** QuickBooks splits
 *   `Smith:Roof` into a customer and a sub-customer, so a contact typed as
 *   "Smith: rental" would silently create a nested record. Colons become dashes.
 * - **A hundred characters is the ceiling.** A company name plus a person's
 *   name can exceed it; the API rejects the whole invoice, not just the name.
 *
 * Falls back through the same chain the database trigger uses when it derives
 * `full_name` (migration 20260813100000), so the two never disagree.
 */
export function quickbooksDisplayName(contact: InvoiceableContact): string {
  const parts = [contact.first_name, contact.last_name]
    .map((p) => (p ?? "").trim())
    .filter(Boolean)
    .join(" ");
  const chosen =
    (contact.full_name ?? "").trim() ||
    parts ||
    (contact.company ?? "").trim() ||
    "";
  return chosen.replace(/:/g, "-").trim().slice(0, 100);
}

/** A QuickBooks customer, as the API takes it. */
export interface QboCustomer {
  DisplayName: string;
  GivenName?: string;
  FamilyName?: string;
  CompanyName?: string;
  PrimaryEmailAddr?: { Address: string };
  PrimaryPhone?: { FreeFormNumber: string };
}

/**
 * A contact, as a QuickBooks customer.
 *
 * Only fields with something in them are sent. QuickBooks validates an empty
 * `PrimaryEmailAddr.Address` and refuses the record, so a blank must be absent
 * rather than present and empty — most of these contacts have a phone and no
 * email, and none of them should fail to reach the accounts because of it.
 */
export function quickbooksCustomer(contact: InvoiceableContact): QboCustomer {
  const customer: QboCustomer = {
    DisplayName: quickbooksDisplayName(contact),
  };
  const given = (contact.first_name ?? "").trim();
  const family = (contact.last_name ?? "").trim();
  const company = (contact.company ?? "").trim();
  const email = (contact.email ?? "").trim();
  const phone = (contact.phone ?? "").trim() || (contact.cell ?? "").trim();

  if (given) customer.GivenName = given;
  if (family) customer.FamilyName = family;
  if (company) customer.CompanyName = company;
  if (email) customer.PrimaryEmailAddr = { Address: email };
  if (phone) customer.PrimaryPhone = { FreeFormNumber: phone };
  return customer;
}

/** A QuickBooks invoice, as the API takes it. */
export interface QboInvoice {
  CustomerRef: { value: string };
  DocNumber?: string;
  TxnDate?: string;
  Line: {
    DetailType: "SalesItemLineDetail";
    Amount: number;
    Description: string;
    SalesItemLineDetail: {
      ItemRef: { value: string };
      Qty: number;
      UnitPrice: number;
      TaxCodeRef: { value: "NON" };
    };
  }[];
  BillEmail?: { Address: string };
  CustomerMemo?: { value: string };
  PrivateNote?: string;
}

/**
 * The line description: what this invoice is for, in a form that is still
 * legible in a bank feed twelve months from now.
 *
 * The service address, not the customer's mailing address — a landlord with
 * four properties gets four invoices that only the address tells apart, and
 * the job carries its own address for exactly that reason (ADR 0003).
 */
export function invoiceDescription(job: InvoiceableJob): string {
  const street = (job.address ?? "").trim();
  const city = (job.city ?? "").trim();
  const state = (job.state ?? "").trim();
  const where = [street, [city, state].filter(Boolean).join(", ")]
    .filter(Boolean)
    .join(" — ");
  const number = job.job_number == null ? "" : `Job ${job.job_number}`;
  return [number, where].filter(Boolean).join(" · ") || "Roofing work";
}

/**
 * One line, not thirty.
 *
 * The temptation is to itemise: the estimate has line items, QuickBooks has
 * line items, they look like the same thing. They are not. Itemising means
 * every one of the 437 products in the catalogue needs a matching QuickBooks
 * item with an income account behind it, kept in step forever, and it puts the
 * cost breakdown — what we pay for a square of shingles — on a document the
 * customer receives. The customer already has the itemised version: it is the
 * proposal they signed. This is the bill for it.
 *
 * So: one service item, the contract amount, and a description naming the job.
 * The books stay lean, which is what they were asked to be.
 */
export function quickbooksInvoice(input: {
  /** QuickBooks' id for the customer, already created or looked up. */
  customerId: string;
  /** QuickBooks' id for the service item every contract is billed against. */
  itemId: string;
  amount: Cents;
  job: InvoiceableJob;
  contact?: InvoiceableContact;
  /** `YYYY-MM-DD`. The day the job was sold, not the day this ran. */
  txnDate?: string | null;
}): QboInvoice {
  const amount = qboAmount(input.amount);
  const description = invoiceDescription(input.job);
  const invoice: QboInvoice = {
    CustomerRef: { value: input.customerId },
    Line: [
      {
        DetailType: "SalesItemLineDetail",
        Amount: amount,
        Description: description,
        SalesItemLineDetail: {
          ItemRef: { value: input.itemId },
          Qty: 1,
          UnitPrice: amount,
          TaxCodeRef: { value: "NON" },
        },
      },
    ],
    PrivateNote: `Roof Board ${description}`,
  };

  if (input.job.job_number != null) {
    invoice.DocNumber = String(input.job.job_number).slice(0, 21);
  }
  if (input.txnDate) invoice.TxnDate = input.txnDate;

  const email = (input.contact?.email ?? "").trim();
  if (email) invoice.BillEmail = { Address: email };

  return invoice;
}

/**
 * Why this job cannot be invoiced, in words a person can act on.
 *
 * Returns an empty array when it can. Nothing here blocks a sale — the job is
 * already sold by the time this runs — it decides whether a row waits in the
 * queue with a reason attached instead of failing against the API and coming
 * back as whatever Intuit's validator says.
 */
export function invoiceBlockers(input: {
  amount?: Cents | null;
  contact?: InvoiceableContact | null;
}): string[] {
  const blockers: string[] = [];
  if (input.amount == null) {
    blockers.push("No contract amount has been worked out for this job yet.");
  } else if (input.amount <= 0) {
    blockers.push(
      "The contract amount is zero, so there is nothing to invoice.",
    );
  }
  if (!input.contact || !quickbooksDisplayName(input.contact)) {
    blockers.push("The customer has no name to file the invoice under.");
  }
  return blockers;
}

/**
 * The service item every contract is billed against, created once on first
 * push and remembered on the connection row.
 *
 * `IncomeAccountRef` is supplied by the caller because which income account is
 * right is a decision made in QuickBooks, not here.
 */
export const QUICKBOOKS_ITEM_NAME = "Roofing Contract";

/** The states a queued invoice can be in. Mirrors the database CHECK. */
export const QUICKBOOKS_INVOICE_STATES = [
  "pending",
  "sent",
  "failed",
  "stranded",
] as const;

export type QuickbooksInvoiceState = (typeof QUICKBOOKS_INVOICE_STATES)[number];

/**
 * What to say about a queued invoice on screen.
 *
 * `stranded` is the one that needs words rather than a colour: it means the
 * handoff was undone after QuickBooks already had the invoice. Nothing in this
 * application voids it, deliberately — money that has reached the accounts is
 * not something an undo button in a CRM should reach back and delete. A person
 * voids it in QuickBooks, and this says so.
 */
export function quickbooksInvoiceNotice(
  state: QuickbooksInvoiceState,
  error?: string | null,
): string {
  switch (state) {
    case "pending":
      // A pending row with an error is the case that used to be invisible: the
      // job sold, the row queued, and working the amount out failed silently.
      // It is still pending — nothing is lost — but somebody has to know.
      return error?.trim()
        ? `Waiting, but the amount could not be worked out: ${error.trim()}`
        : "Waiting to go to QuickBooks.";
    case "sent":
      // A sent row can still carry an error: the invoice reached QuickBooks,
      // and re-dating it afterwards failed. That is worth saying — the usual
      // cause is somebody having edited the invoice in QuickBooks, which moves
      // its SyncToken and makes the correction refuse forever. Swallowing it
      // left the wrong date on the books with nothing on screen to show for it.
      return error?.trim()
        ? `In QuickBooks, but the date could not be corrected: ${error.trim()}`
        : "In QuickBooks.";
    case "failed":
      return error?.trim()
        ? `QuickBooks refused it: ${error.trim()}`
        : "QuickBooks refused it.";
    case "stranded":
      return (
        "The handoff was undone after this invoice reached QuickBooks. " +
        "Void it in QuickBooks — Roof Board will not delete an invoice."
      );
  }
}

/** The part of a work order that says whether — and when — the work was done. */
export interface CompletableWorkOrder {
  /** `work_orders.completed_at`, a timestamptz, or null while it is open. */
  completed_at?: string | null;
}

/**
 * The calendar date an invoice carries in QuickBooks.
 *
 * **The date is the day the work was completed.** Standing instruction from
 * Jonathon, 2026-08-30: the books should say when CopperRidge earned the money,
 * not when somebody sold it. He runs accrual, so the two are not the same
 * number and an invoice dated at the sale overstates a month that has not
 * finished its work.
 *
 * The awkwardness this function exists to absorb: the invoice is written the
 * moment a job is **sold** (ADR 0038 and the top of this file), which is before
 * a single shingle is on the roof. At that instant no completion date exists.
 * So the sale date stands in, and the invoice is re-dated in QuickBooks once
 * the work orders close — `push` compares this against the date it last sent
 * and corrects the difference (ADR 0078).
 *
 * Three cases, in the order they are asked:
 *
 * 1. **Every work order is complete.** The latest `completed_at` wins: a job
 *    with a tear-off crew and a gutter crew is finished when the last of them
 *    leaves, not the first.
 * 2. **Any work order is still open.** The sale date stands in. The job is
 *    unfinished and this will be asked again.
 * 3. **There are no work orders at all.** The sale date, and it stays that way.
 *    That is not an omission to fix: job 1017 never had one because Sam did the
 *    work himself on the spot, and on a same-day job the two dates are the same
 *    date anyway.
 *
 * "The sale date" is `sends_at`, and since 2026-09-07 an invoice can be raised
 * before there is one: signing opens the books too (ADR 0114). So the signature
 * date stands in for a job that has not reached Sends. Without it the invoice
 * would carry no `TxnDate` at all and QuickBooks would stamp its own today —
 * which on a contract signed at the weekend is the wrong month often enough to
 * matter. Sends still wins once it exists; it is the sale, and the signature is
 * only the earliest thing known about it.
 *
 * Dates are resolved in **Central Time**, because a crew that finishes at seven
 * on a summer evening in Alabama is stamped past midnight UTC, and slicing the
 * timestamp would book their work on the following day.
 *
 * @returns `YYYY-MM-DD`, or null when there is no date to be had.
 */
export function invoiceTxnDate(input: {
  /** `jobs.sends_at` — when the job was sold. */
  sendsAt?: string | null;
  /** `jobs.signed_contract_at`. Stands in before there is a sale date. */
  signedContractAt?: string | null;
  /** Every work order on the job, however many estimates raised them. */
  workOrders?: readonly CompletableWorkOrder[];
}): string | null {
  const orders = input.workOrders ?? [];
  const completions = orders
    .map((order) => (order.completed_at ?? "").trim())
    .filter(Boolean);

  // Case 1: all of them closed, so the last one to close is the date.
  if (orders.length > 0 && completions.length === orders.length) {
    const latest = completions
      .map((at) => ({ at, ms: toInstant(at)?.getTime() ?? Number.NaN }))
      .filter((c) => !Number.isNaN(c.ms))
      .sort((a, b) => b.ms - a.ms)[0];
    if (latest) return centralDate(latest.at);
  }

  // Cases 2 and 3: the sale date stands in, and the signature stands in for
  // that on a job invoiced at signing and not yet sent.
  //
  // `signed_contract_at` is a `date` column, so it is read as the calendar day
  // it already says and never converted. `new Date("2026-09-01")` is midnight
  // UTC, which in Alabama is the evening of 31 August — the fault that made
  // "signed this month" read zero, and it would book a contract into the wrong
  // month here just as readily.
  return centralDate(input.sendsAt) ?? calendarDate(input.signedContractAt);
}

/**
 * A timestamp as the calendar date it was in Alabama.
 *
 * `America/Chicago` rather than a fixed offset, so the answer stays right
 * across the daylight-saving switch without anybody maintaining a table of
 * dates. Matches how `countyStormReportHtml` reads its timestamps.
 */
function centralDate(timestamp?: string | null): string | null {
  const at = toInstant(timestamp);
  if (!at) return null;
  const parts = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/Chicago",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(at);
  // en-CA formats as YYYY-MM-DD, which is the shape QuickBooks' TxnDate takes.
  return /^\d{4}-\d{2}-\d{2}$/.test(parts) ? parts : null;
}

/**
 * A Postgres timestamp as a Date.
 *
 * PostgREST returns `2026-08-27 14:31:01.484+00` — a space where ISO 8601 wants
 * a T, and a two-digit offset where it wants four. Neither is optional to
 * JavaScript: `new Date()` on that string returns Invalid Date in Node, which
 * is how every completion date silently became null the first time this was
 * written.
 */
/**
 * A `date` column as the day it already names.
 *
 * Bare "YYYY-MM-DD" carries no time and no zone, so there is nothing to
 * convert. Anything else is a real timestamp and is read as one.
 */
function calendarDate(value?: string | null): string | null {
  const raw = (value ?? "").trim();
  if (!raw) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  return centralDate(raw);
}

function toInstant(timestamp?: string | null): Date | null {
  const raw = (timestamp ?? "").trim();
  if (!raw) return null;
  const iso = raw
    .replace(" ", "T")
    // +00 and -05 become +00:00 and -05:00; +0000 becomes +00:00.
    .replace(/([+-])(\d{2})$/, "$1$2:00")
    .replace(/([+-])(\d{2})(\d{2})$/, "$1$2:$3");
  const at = new Date(iso);
  return Number.isNaN(at.getTime()) ? null : at;
}

/** What a sent invoice's total should be, against what QuickBooks holds. */
export interface InvoiceCorrection {
  /** Whether the invoice in QuickBooks should be changed. */
  correct: boolean;
  /** Why not, in words somebody can act on. Null when there is nothing to say. */
  reason: string | null;
}

/**
 * Whether a sent invoice may be corrected to a new total.
 *
 * The rule that matters is the one about **not** correcting. An invoice is a
 * document somebody outside this company may have received, paid against, or
 * edited in QuickBooks. Silently rewriting one is worse than leaving it wrong,
 * because a wrong number somebody can see gets questioned and a number that
 * changes underneath them does not.
 *
 * So three things stop a correction, and each says so rather than failing
 * quietly:
 *
 * 1. **Money has been paid against it.** QuickBooks' `Balance` is less than its
 *    `TotalAmt`. Changing the total of a part-paid invoice is an accounting
 *    decision — a credit note, or a second invoice — and not one a CRM makes.
 * 2. **Somebody edited it in QuickBooks.** What is there now is not what we
 *    last sent, so QuickBooks is not simply behind us: two people have an
 *    opinion and the human's wins.
 * 3. **It already agrees.** Nothing to do, and writing anyway would bump the
 *    SyncToken and leave a "modified" line in the audit log for a change that
 *    changes nothing.
 *
 * @param wanted   what the ladder says the contract is worth now, in cents
 * @param lastSent what this invoice was last sent with, in cents
 * @param inQbo    what QuickBooks holds now: total and outstanding balance
 */
export function invoiceCorrection(input: {
  wanted: Cents;
  lastSent?: Cents | null;
  inQbo: { total: Cents; balance?: Cents | null | undefined };
}): InvoiceCorrection {
  const { wanted, inQbo } = input;
  const lastSent = input.lastSent ?? null;

  if (wanted <= 0) {
    return {
      correct: false,
      reason: "No contract amount could be worked out for this job.",
    };
  }

  if (wanted === inQbo.total) return { correct: false, reason: null };

  const balance = inQbo.balance ?? inQbo.total;
  if (balance < inQbo.total) {
    return {
      correct: false,
      reason:
        "The estimate changed, but this invoice has already been paid against " +
        "in QuickBooks. Correct it there — a part-paid invoice needs a credit " +
        "note or a second invoice, not a new total.",
    };
  }

  if (lastSent != null && inQbo.total !== lastSent) {
    return {
      correct: false,
      reason:
        "The estimate changed, but somebody has already edited this invoice " +
        "in QuickBooks. Roof Board has left it alone — check which figure is " +
        "right.",
    };
  }

  return { correct: true, reason: null };
}
